import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  OAuthProvider,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from "firebase/auth";
import { auth } from "../../firebaseApp.js";
import { getDb } from "../../firestoreRegistry.js";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const googleProvider = new GoogleAuthProvider();
const microsoftProvider = new OAuthProvider('microsoft.com');

async function upsertUserProfile(momentsDb, user, provider, fallbackDisplayName = "") {
  const userRef = doc(momentsDb, "users", user.uid);
  const existing = await getDoc(userRef).catch(() => null);
  const existingData = existing?.data ? existing.data() : null;
  const hasCreatedAt = !!existingData?.createdAt;

  const payload = {
    displayName: user.displayName || fallbackDisplayName || "",
    email: user.email || "",
    photoUrl: user.photoURL || "",
    provider,
    updatedAt: serverTimestamp(),
    plan: "free"
  };

  if (!hasCreatedAt) {
    payload.createdAt = serverTimestamp();
  }

  await setDoc(userRef, payload, { merge: true });
}

/**
 * Sign in with Google and save user info to Firestore
 */
export async function signInWithGoogle() {
  const momentsDb = getDb("moments");
  const res = await signInWithPopup(auth, googleProvider);
  await upsertUserProfile(momentsDb, res.user, "google");
  return res.user;
}

/**
 * Sign in with Microsoft and save user info to Firestore
 */
export async function signInWithMicrosoft() {
  const momentsDb = getDb("moments");
  const res = await signInWithPopup(auth, microsoftProvider);
  await upsertUserProfile(momentsDb, res.user, "microsoft");
  return res.user;
}

/**
 * Generic sign-in function that handles both providers
 */
export async function signInWithProvider(providerType) {
  if (providerType === 'google') {
    return await signInWithGoogle();
  } else if (providerType === 'microsoft') {
    return await signInWithMicrosoft();
  } else {
    throw new Error(`Unknown provider: ${providerType}`);
  }
}

/**
 * Send a sign-in link to the user's email (passwordless authentication)
 * @param {string} email - The email address to send the magic link to
 * @param {string} redirectUrl - Optional custom redirect URL (defaults to /auth/callback)
 * @returns {Promise<void>}
 */
export async function sendSignInLink(email, redirectUrl = null) {
  // Construct the full callback URL
  // Use production domain if available, otherwise use current origin
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const baseUrl = isLocalhost ? window.location.origin : 'https://partydecider.com';
  const callbackPath = redirectUrl || '/__/auth/action';
  const url = `${baseUrl}${callbackPath}`;
  
  const actionCodeSettings = {
    url: url,
    handleCodeInApp: true,
  };
  
  try {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    
    // Save the email to localStorage so we can retrieve it after redirect
    window.localStorage.setItem('emailForSignIn', email);
    
    console.log('[Auth] Sign-in link sent to:', email);
    return { success: true };
  } catch (error) {
    console.error('[Auth] Failed to send sign-in link:', error);
    throw error;
  }
}

/**
 * Complete the email-link sign-in process after user clicks the link
 * @param {string} emailLink - The full URL from the email link
 * @returns {Promise<User>} The authenticated user
 */
export async function completeSignInWithEmailLink(emailLink) {
  // Verify this is a sign-in link
  if (!isSignInWithEmailLink(auth, emailLink)) {
    throw new Error('Invalid sign-in link');
  }
  
  // Get the email from localStorage or prompt the user
  let email = window.localStorage.getItem('emailForSignIn');
  
  if (!email) {
    // If email is not found, prompt the user to enter it
    email = window.prompt('Please provide your email for confirmation');
    if (!email) {
      throw new Error('Email is required to complete sign-in');
    }
  }
  
  try {
    // Complete the sign-in
    const result = await signInWithEmailLink(auth, email, emailLink);
    const user = result.user;
    
    // Clear the email from localStorage
    window.localStorage.removeItem('emailForSignIn');
    
    // Save user info to Firestore
    const momentsDb = getDb("moments");
    await upsertUserProfile(
      momentsDb,
      user,
      "email-link",
      user.displayName || email.split('@')[0]
    );
    
    console.log('[Auth] Email-link sign-in successful:', user.uid);
    return user;
  } catch (error) {
    console.error('[Auth] Email-link sign-in failed:', error);
    
    // Provide user-friendly error messages
    if (error.code === 'auth/invalid-action-code') {
      throw new Error('This sign-in link has expired or was already used. Please request a new one.');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('The email address is invalid. Please check and try again.');
    } else {
      throw new Error(error.message || 'Failed to complete sign-in');
    }
  }
}
